import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { CalendarDays, Plus, Pencil, Trash2, X, Check, Info, Repeat } from "lucide-react";
import EmpPicker from "@/components/EmpPicker";

const DAYS = [["sen", "Senin"], ["sel", "Selasa"], ["rab", "Rabu"], ["kam", "Kamis"], ["jum", "Jumat"], ["sab", "Sabtu"], ["min", "Minggu"]];
const inp = "h-10 px-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]";
const blank = { name: "", work_days: [], shift_start: "09:00", shift_end: "17:00", perDay: false, day_shifts: {}, break_start: "12:00", break_end: "13:00", late_tolerance: 10, effective_from: "", mode: "names", user_ids: [], role_ids: [] };

const toMin = (t) => { if (!t) return null; const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const daysLabel = (wd) => {
  if (!wd?.length) return "—";
  const idx = wd.map((d) => DAYS.findIndex(([k]) => k === d)).filter((i) => i >= 0).sort((a, b) => a - b);
  const contig = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  const nm = (i) => DAYS[i][1];
  return contig && idx.length > 1 ? `${nm(idx[0])} – ${nm(idx[idx.length - 1])}` : idx.map(nm).join(", ");
};
const durLabel = (start, end, bs, be) => {
  const d = toMin(end) - toMin(start);
  if (!(d > 0)) return "—";
  const brk = bs && be ? Math.max(0, toMin(be) - toMin(bs)) : 0;
  const net = Math.max(0, d - brk);
  return `${(net / 60).toLocaleString("id-ID")} jam/hari`;
};

export default function AdminSchedule() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);
  const [editingCount, setEditingCount] = useState(0);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [tab, setTab] = useState("template");

  const load = useCallback(() => {
    setData(null);
    api.get("/admin/shifts").then((r) => setData(r.data))
      .catch((e) => { setData(false); toast.error(e?.response?.data?.detail || "Gagal memuat shift"); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (k) => setForm((f) => {
    const on = f.work_days.includes(k);
    const day_shifts = { ...f.day_shifts };
    if (on) delete day_shifts[k];
    return { ...f, work_days: on ? f.work_days.filter((x) => x !== k) : [...f.work_days, k], day_shifts };
  });
  const setDayShift = (k, field, v) => setForm((f) => ({
    ...f, day_shifts: { ...f.day_shifts, [k]: { start: f.shift_start, end: f.shift_end, ...(f.day_shifts[k] || {}), [field]: v } },
  }));

  const openForm = (s) => {
    if (s) {
      setEditing(s.id);
      setEditingCount(s.employee_count || 0);
      setForm({
        name: s.name, work_days: s.work_days || [], shift_start: s.shift_start, shift_end: s.shift_end,
        perDay: !!(s.day_shifts && Object.keys(s.day_shifts).length), day_shifts: s.day_shifts || {},
        break_start: s.break_start || "", break_end: s.break_end || "", late_tolerance: s.late_tolerance ?? 10,
        effective_from: s.effective_from || "", mode: "names", user_ids: [], role_ids: [],
      });
    } else { setEditing(null); setEditingCount(0); setForm(blank); }
    setView("form");
  };

  const save = async () => {
    if (form.name.trim().length < 2) return toast.error("Nama shift wajib diisi");
    if (!form.work_days.length) return toast.error("Pilih minimal satu hari kerja");
    setSaving(true);
    const payload = {
      name: form.name.trim(), work_days: form.work_days, shift_start: form.shift_start, shift_end: form.shift_end,
      day_shifts: form.perDay ? form.day_shifts : {}, break_start: form.break_start || null, break_end: form.break_end || null,
      late_tolerance: parseInt(form.late_tolerance) || 0, effective_from: form.effective_from || null,
      user_ids: form.mode === "names" ? form.user_ids : [], role_ids: form.mode === "roles" ? form.role_ids : [],
    };
    try {
      const r = editing ? await api.put(`/admin/shifts/${editing}`, payload) : await api.post("/admin/shifts", payload);
      const n = r.data.applied ?? 0;
      toast.success(editing ? `Shift diperbarui — ${n} karyawan menerima jadwal ini` : `Shift dibuat — diterapkan ke ${n} karyawan`);
      setView("list");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan shift"); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/shifts/${delTarget.id}`);
      toast.success(`Shift "${delTarget.name}" dihapus — jadwal karyawan yang sudah tersalin tidak berubah`);
      setDelTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus shift"); }
  };

  const emps = data?.options?.employees || [];
  const selectedCount = useMemo(() => {
    if (form.mode === "names") return form.user_ids.length;
    return emps.filter((e) => form.role_ids.includes(e.role)).length;
  }, [form.mode, form.user_ids, form.role_ids, emps]);
  const libur = DAYS.map(([k]) => k).filter((k) => !form.work_days.includes(k));
  const durasi = form.perDay ? "Bervariasi per hari" : durLabel(form.shift_start, form.shift_end, form.break_start, form.break_end);

  if (view === "form") {
    return (
      <div data-testid="sh-form" className="space-y-4 pb-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] text-slate-400">Shift Kerja / {editing ? "Ubah Shift" : "Tambah Shift"}</p>
            <h1 className="font-display text-2xl font-bold text-slate-900">{editing ? "Ubah Shift" : "Tambah Shift"}</h1>
            <p className="text-xs text-slate-400">Buat jadwal kerja rutin untuk karyawan.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="sh-cancel-top" onClick={() => setView("list")} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 transition-colors">Batal</button>
            <button data-testid="sh-save-top" onClick={save} disabled={saving} className="h-9 px-4 rounded-lg bg-[#7C2D3E] hover:bg-[#66222f] text-white text-xs font-semibold transition-colors disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan Shift"}</button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 items-start">
          <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-5">
            <div>
              <h3 className="font-display font-bold text-sm text-slate-900 mb-3">Informasi Shift</h3>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Nama Shift <span className="text-red-400">*</span></label>
              <input data-testid="sh-name" value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="mis. Shift Gudang" className={`${inp} w-full`} />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Hari Kerja <span className="text-red-400">*</span></label>
              <div className="flex gap-2 flex-wrap">
                {DAYS.map(([k, l]) => {
                  const on = form.work_days.includes(k);
                  return (
                    <button key={k} data-testid={`sh-day-${k}`} onClick={() => toggleDay(k)}
                      className={`h-9 px-4 rounded-lg text-[11px] font-semibold border transition-colors flex items-center gap-1.5 ${on ? "border-[#C4738A] text-[#C4738A] bg-rose-50/60" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                      {on && <Check className="w-3 h-3" />}{l}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">Hari yang tidak dipilih menjadi hari libur.</p>
            </div>

            <div>
              <h4 className="text-[11px] font-semibold text-slate-500 mb-1.5">Jam Kerja</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-slate-400 mb-1">Jam Masuk <span className="text-red-400">*</span></label>
                  <input data-testid="sh-start" type="time" value={form.shift_start} onChange={(e) => setF("shift_start", e.target.value)} className={`${inp} w-full`} />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-400 mb-1">Jam Pulang <span className="text-red-400">*</span></label>
                  <input data-testid="sh-end" type="time" value={form.shift_end} onChange={(e) => setF("shift_end", e.target.value)} className={`${inp} w-full`} />
                </div>
              </div>
              <label className="flex items-center gap-2 mt-3 cursor-pointer text-[11px] text-slate-500">
                <button data-testid="sh-perday-toggle" onClick={() => setF("perDay", !form.perDay)}
                  className={`relative inline-flex w-9 h-5 rounded-full transition-colors flex-shrink-0 ${form.perDay ? "bg-[#C4738A]" : "bg-slate-200"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${form.perDay ? "left-[18px]" : "left-0.5"}`} />
                </button>
                Atur jam berbeda per hari (opsional)
              </label>
              {form.perDay && (
                <div data-testid="sh-perday-list" className="mt-3 space-y-2">
                  {DAYS.filter(([k]) => form.work_days.includes(k)).map(([k, l]) => {
                    const ds = form.day_shifts[k] || {};
                    return (
                      <div key={k} className="grid grid-cols-[70px_1fr_1fr] gap-2 items-center">
                        <span className="text-[11px] font-semibold text-slate-600">{l}</span>
                        <input data-testid={`sh-day-start-${k}`} type="time" value={ds.start || form.shift_start} onChange={(e) => setDayShift(k, "start", e.target.value)} className={inp} />
                        <input data-testid={`sh-day-end-${k}`} type="time" value={ds.end || form.shift_end} onChange={(e) => setDayShift(k, "end", e.target.value)} className={inp} />
                      </div>
                    );
                  })}
                  {!form.work_days.length && <p className="text-[10px] text-slate-400">Pilih hari kerja dulu di atas.</p>}
                </div>
              )}
            </div>

            <div>
              <h4 className="text-[11px] font-semibold text-slate-500 mb-1.5">Istirahat</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-slate-400 mb-1">Mulai Istirahat</label>
                  <input data-testid="sh-break-start" type="time" value={form.break_start} onChange={(e) => setF("break_start", e.target.value)} className={`${inp} w-full`} />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-400 mb-1">Selesai Istirahat</label>
                  <input data-testid="sh-break-end" type="time" value={form.break_end} onChange={(e) => setF("break_end", e.target.value)} className={`${inp} w-full`} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Toleransi Terlambat <span className="text-red-400">*</span></label>
                <div className="relative">
                  <input data-testid="sh-tolerance" type="number" min="0" max="240" value={form.late_tolerance} onChange={(e) => setF("late_tolerance", e.target.value)} className={`${inp} w-full pr-14`} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">menit</span>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Mulai Berlaku <span className="text-red-400">*</span></label>
                <input data-testid="sh-effective" type="date" value={form.effective_from} onChange={(e) => setF("effective_from", e.target.value)} className={`${inp} w-full`} />
              </div>
            </div>

            <div>
              <h4 className="text-[11px] font-semibold text-slate-500 mb-1.5">Terapkan ke Karyawan</h4>
              <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-50 border border-slate-100 mb-3">
                {[["names", "Pilih Nama"], ["roles", "Berdasarkan Jabatan"]].map(([v, l]) => (
                  <button key={v} data-testid={`sh-mode-${v}`} onClick={() => setF("mode", v)}
                    className={`h-8 rounded-lg text-[11px] font-semibold transition-colors ${form.mode === v ? "bg-white text-[#C4738A] shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>{l}</button>
                ))}
              </div>
              {form.mode === "names" ? (
                <EmpPicker tid="sh-emp" options={emps} value={form.user_ids} onChange={(ids) => setF("user_ids", ids)} placeholder="Ketik nama karyawan..." />
              ) : (
                <div className="grid grid-cols-2 gap-1.5 p-3 rounded-xl bg-slate-50 border border-slate-100 max-h-44 overflow-y-auto">
                  {(data?.options?.roles || []).map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
                      <input type="checkbox" data-testid={`sh-role-${r.id}`} className="accent-[#C4738A]"
                        checked={form.role_ids.includes(r.id)}
                        onChange={(e) => setF("role_ids", e.target.checked ? [...form.role_ids, r.id] : form.role_ids.filter((x) => x !== r.id))} />
                      {r.label}
                    </label>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-1.5" data-testid="sh-selected-count">{selectedCount} karyawan dipilih.{editing ? ` ${editingCount} karyawan yang sudah memakai shift ini otomatis ikut diperbarui.` : ""}</p>
            </div>
          </div>

          <div data-testid="sh-summary" className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3 sticky top-4">
            <h3 className="text-[11px] font-semibold text-slate-400">Ringkasan Shift</h3>
            <div className="flex items-start justify-between gap-2">
              <h2 className="font-display text-lg font-bold text-slate-900">{form.name || "Nama Shift"}</h2>
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-[#C4738A] whitespace-nowrap">Jadwal Mingguan</span>
            </div>
            <div className="divide-y divide-slate-50 text-[11px]">
              {[
                ["Hari Kerja", daysLabel(form.work_days)],
                ["Hari Libur", daysLabel(libur)],
                ["Jam Kerja", form.perDay ? "Berbeda per hari" : `${form.shift_start} – ${form.shift_end}`],
                ["Istirahat", form.break_start && form.break_end ? `${form.break_start} – ${form.break_end}` : "—"],
                ["Durasi Bersih", durasi],
                ["Toleransi", `${form.late_tolerance || 0} menit`],
                ["Berlaku Mulai", form.effective_from ? formatDateID(form.effective_from) : "—"],
                ["Karyawan", `${selectedCount + (editing ? editingCount : 0)} orang`],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between py-2"><span className="text-slate-400">{l}</span><span className="font-semibold text-slate-700 text-right">{v}</span></div>
              ))}
            </div>
            <div className="flex items-start gap-2 bg-blue-50/60 border border-blue-100 rounded-xl px-3 py-2.5 text-[10px] text-blue-700">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Jadwal berulang otomatis setiap minggu sejak tanggal mulai berlaku.
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[10px] text-slate-400 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Perubahan jadwal tidak mengubah absensi yang sudah tercatat.</p>
          <div className="flex gap-2">
            <button data-testid="sh-cancel" onClick={() => setView("list")} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 transition-colors">Batal</button>
            <button data-testid="sh-save" onClick={save} disabled={saving} className="h-9 px-4 rounded-lg bg-[#7C2D3E] hover:bg-[#66222f] text-white text-xs font-semibold transition-colors disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan Shift"}</button>
          </div>
        </div>
      </div>
    );
  }

  const items = data?.items || [];
  return (
    <div data-testid="sh-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Shift Kerja</h1>
          <p className="text-xs text-slate-400">Template jadwal mingguan karyawan — diterapkan ke absensi, toleransi terlambat & payroll.</p>
        </div>
        {tab === "template" && (
          <button data-testid="sh-add" onClick={() => openForm(null)} className="h-9 px-4 rounded-lg bg-[#7C2D3E] hover:bg-[#66222f] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Tambah Shift
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["template", "Template Shift"], ["rotasi", "Rotasi Shift"]].map(([v, l]) => (
          <button key={v} data-testid={`sh-tab-${v}`} onClick={() => setTab(v)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === v ? "border-[#C4738A] text-[#C4738A]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "rotasi" && <RotationTab />}
      {tab === "template" && data === null && <p className="py-12 text-center text-xs text-slate-400">Memuat...</p>}
      {tab === "template" && data && !items.length && (
        <div className="bg-white rounded-2xl border border-slate-100 py-14 text-center">
          <CalendarDays className="w-8 h-8 mx-auto mb-2 text-slate-200" />
          <p className="text-xs text-slate-400">Belum ada shift — klik <b>Tambah Shift</b> untuk membuat jadwal pertama.</p>
        </div>
      )}
      {tab === "template" && (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((s) => (
          <div key={s.id} data-testid={`sh-card-${s.id}`} className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-display font-bold text-sm text-slate-900">{s.name}</h3>
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-[#C4738A] whitespace-nowrap">Jadwal Mingguan</span>
            </div>
            <div className="divide-y divide-slate-50 text-[11px]">
              <div className="flex justify-between py-1.5"><span className="text-slate-400">Hari Kerja</span><span className="font-semibold text-slate-700">{daysLabel(s.work_days)}</span></div>
              <div className="flex justify-between py-1.5"><span className="text-slate-400">Jam Kerja</span><span className="font-semibold text-slate-700">{s.day_shifts && Object.keys(s.day_shifts).length ? "Berbeda per hari" : `${s.shift_start} – ${s.shift_end}`}</span></div>
              <div className="flex justify-between py-1.5"><span className="text-slate-400">Toleransi</span><span className="font-semibold text-slate-700">{s.late_tolerance ?? 10} menit</span></div>
              <div className="flex justify-between py-1.5"><span className="text-slate-400">Berlaku Mulai</span><span className="font-semibold text-slate-700">{s.effective_from ? formatDateID(s.effective_from) : "—"}</span></div>
              <div className="flex justify-between py-1.5"><span className="text-slate-400">Karyawan</span><span className="font-semibold text-slate-700">{s.employee_count} orang</span></div>
            </div>
            <div className="flex gap-1 justify-end pt-1">
              <button data-testid={`sh-edit-${s.id}`} onClick={() => openForm(s)} title="Ubah" className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
              <button data-testid={`sh-del-${s.id}`} onClick={() => setDelTarget(s)} title="Hapus" className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      )}

      {delTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDelTarget(null)}>
          <div data-testid="sh-del-modal" className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-sm text-slate-900">Hapus Shift</h4>
              <button data-testid="sh-del-close" onClick={() => setDelTarget(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">Hapus shift <b>{delTarget.name}</b>? Jadwal yang sudah tersalin ke {delTarget.employee_count} karyawan <b>tidak berubah</b> dan absensi tercatat tetap aman.</p>
            <div className="flex gap-2 pt-1">
              <button data-testid="sh-del-cancel" onClick={() => setDelTarget(null)} className="flex-1 h-9 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold hover:border-slate-300">Batal</button>
              <button data-testid="sh-del-confirm" onClick={doDelete} className="flex-1 h-9 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 transition-colors">Ya, Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ROT_FREQS = [["weekly", "Mingguan"], ["biweekly", "2 Mingguan"], ["monthly", "Bulanan (4 minggu)"]];

function RotationTab() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);
  const blankRot = { name: "", freq: "weekly", anchor_date: "", shift_ids: [], groups: [{ name: "Grup A", user_ids: [] }, { name: "Grup B", user_ids: [] }], active: true };
  const [form, setForm] = useState(blankRot);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(() => {
    setData(null);
    api.get("/admin/rotations").then((r) => setData(r.data))
      .catch((e) => { setData(false); toast.error(e?.response?.data?.detail || "Gagal memuat rotasi"); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const shifts = data?.options?.shifts || [];
  const emps = data?.options?.employees || [];
  const shiftName = (sid) => (shifts.find((s) => s.id === sid) || {}).name || "?";

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleShift = (sid) => setForm((f) => ({ ...f, shift_ids: f.shift_ids.includes(sid) ? f.shift_ids.filter((x) => x !== sid) : [...f.shift_ids, sid] }));
  const setGroup = (i, patch) => setForm((f) => ({ ...f, groups: f.groups.map((g, gi) => gi === i ? { ...g, ...patch } : g) }));
  const addGroup = () => setForm((f) => ({ ...f, groups: [...f.groups, { name: `Grup ${String.fromCharCode(65 + f.groups.length)}`, user_ids: [] }] }));
  const delGroup = (i) => setForm((f) => ({ ...f, groups: f.groups.filter((_, gi) => gi !== i) }));

  const openForm = (r) => {
    if (r) setForm({ name: r.name, freq: r.freq, anchor_date: r.anchor_date, shift_ids: r.shift_ids || [], groups: (r.groups || []).map((g) => ({ name: g.name, user_ids: g.user_ids || [] })), active: r.active });
    else setForm(blankRot);
    setEditing(r ? r.id : null);
    setView("form");
  };

  const save = async () => {
    if (form.name.trim().length < 2) return toast.error("Nama rotasi wajib diisi");
    if (!form.anchor_date) return toast.error("Pilih tanggal mulai rotasi");
    if (form.shift_ids.length < 2) return toast.error("Pilih minimal 2 shift untuk dirotasi");
    if (form.groups.some((g) => !g.user_ids.length)) return toast.error("Setiap grup minimal punya 1 karyawan");
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/rotations/${editing}`, form);
      else await api.post("/admin/rotations", form);
      toast.success(editing ? "Rotasi diperbarui" : "Rotasi dibuat — shift grup otomatis bertukar sesuai frekuensi");
      setView("list");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan rotasi"); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/rotations/${delTarget.id}`);
      toast.success("Rotasi dihapus — karyawan kembali ke jadwal bawaannya");
      setDelTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const toggleActive = async (r) => {
    try {
      const res = await api.put(`/admin/rotations/${r.id}/toggle`);
      toast.success(res.data.active ? "Rotasi diaktifkan" : "Rotasi dinonaktifkan — karyawan kembali ke jadwal bawaan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
  };

  if (view === "form") {
    return (
      <div data-testid="rot-form" className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">{editing ? "Ubah Rotasi" : "Buat Rotasi"}</h2>
            <p className="text-xs text-slate-400">Grup karyawan bertukar shift otomatis sesuai frekuensi.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="rot-cancel" onClick={() => setView("list")} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 transition-colors">Batal</button>
            <button data-testid="rot-save" onClick={save} disabled={saving} className="h-9 px-4 rounded-lg bg-[#7C2D3E] hover:bg-[#66222f] text-white text-xs font-semibold transition-colors disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan Rotasi"}</button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Nama Rotasi <span className="text-red-400">*</span></label>
              <input data-testid="rot-name" value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="mis. Rotasi Toko Pagi–Siang" className={`${inp} w-full`} />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Mulai Rotasi <span className="text-red-400">*</span></label>
              <input data-testid="rot-anchor" type="date" value={form.anchor_date} onChange={(e) => setF("anchor_date", e.target.value)} className={`${inp} w-full`} />
              <p className="text-[10px] text-slate-400 mt-1">Sebaiknya hari Senin — rotasi dihitung per minggu sejak tanggal ini.</p>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Frekuensi Tukar</label>
            <div className="flex gap-2 flex-wrap">
              {ROT_FREQS.map(([v, l]) => (
                <button key={v} data-testid={`rot-freq-${v}`} onClick={() => setF("freq", v)}
                  className={`h-9 px-4 rounded-lg text-[11px] font-semibold border transition-colors ${form.freq === v ? "border-[#C4738A] text-[#C4738A] bg-rose-50/60" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{l}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Shift yang Dirotasi <span className="text-red-400">*</span> <span className="font-normal text-slate-400">(urutan klik = urutan rotasi)</span></label>
            {form.shift_ids.length > 0 && (
              <div className="flex gap-1.5 flex-wrap mb-2">
                {form.shift_ids.map((sid, i) => (
                  <span key={sid} className="inline-flex items-center gap-1 bg-rose-50 text-[#C4738A] text-[10px] font-semibold rounded-full px-2.5 py-1">
                    {i + 1}. {shiftName(sid)}
                    <span role="button" tabIndex={0} onClick={() => toggleShift(sid)} className="hover:text-[#7C2D3E] cursor-pointer"><X className="w-3 h-3" /></span>
                  </span>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {shifts.map((s) => {
                const on = form.shift_ids.includes(s.id);
                return (
                  <button key={s.id} data-testid={`rot-shift-${s.id}`} onClick={() => toggleShift(s.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left transition-colors ${on ? "border-[#C4738A] bg-rose-50/50" : "border-slate-200 hover:border-slate-300"}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? "bg-[#C4738A] border-[#C4738A]" : "border-slate-300 bg-white"}`}>{on && <Check className="w-2.5 h-2.5 text-white" />}</span>
                    <span><span className="block text-[11px] font-semibold text-slate-700">{s.name}</span>
                      <span className="block text-[9px] text-slate-400">{s.shift_start} – {s.shift_end} • {daysLabel(s.work_days)}</span></span>
                  </button>
                );
              })}
              {!shifts.length && <p className="text-[10px] text-slate-400 col-span-2">Belum ada template shift — buat dulu di tab Template Shift.</p>}
            </div>
          </div>

          {form.shift_ids.length >= 2 && (
            <div className="bg-slate-50 rounded-xl border border-slate-100 px-3 py-2.5 text-[10px] text-slate-500">
              <b>Periode 1:</b> {form.groups.map((g, gi) => `${g.name} → ${shiftName(form.shift_ids[gi % form.shift_ids.length])}`).join(" • ")} — lalu bergeser satu shift setiap periode.
            </div>
          )}

          <div className="space-y-3">
            <label className="block text-[11px] font-semibold text-slate-500">Grup Karyawan <span className="text-red-400">*</span></label>
            {form.groups.map((g, i) => (
              <div key={i} className="rounded-xl border border-slate-100 p-3.5 space-y-2.5 bg-slate-50/40">
                <div className="flex items-center gap-2">
                  <input data-testid={`rot-group-name-${i}`} value={g.name} onChange={(e) => setGroup(i, { name: e.target.value })} className={`${inp} w-40`} />
                  <span className="text-[10px] text-slate-400">{g.user_ids.length} anggota</span>
                  {form.groups.length > 2 && (
                    <button data-testid={`rot-group-del-${i}`} onClick={() => delGroup(i)} className="ml-auto w-7 h-7 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
                <EmpPicker tid={`rot-emp-${i}`} options={emps} value={g.user_ids} onChange={(ids) => setGroup(i, { user_ids: ids })} placeholder="Ketik nama anggota grup..." />
              </div>
            ))}
            <button data-testid="rot-group-add" onClick={addGroup} className="h-8 px-3.5 rounded-lg border border-dashed border-slate-200 text-slate-500 text-[11px] font-semibold hover:border-[#C4738A] hover:text-[#C4738A] transition-colors flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Tambah Grup
            </button>
          </div>

          <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
            <input type="checkbox" data-testid="rot-active" className="accent-[#C4738A]" checked={form.active} onChange={(e) => setF("active", e.target.checked)} />
            Aktif — rotasi langsung dipakai absensi & payroll
          </label>
        </div>
      </div>
    );
  }

  const items = data?.items || [];
  return (
    <div data-testid="rot-page" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Rotasi aktif otomatis menentukan jam kerja & hari kerja karyawan per tanggal — dipakai absensi, toleransi telat, dan lembur payroll.</p>
        <button data-testid="rot-add" onClick={() => openForm(null)} className="h-9 px-4 rounded-lg bg-[#7C2D3E] hover:bg-[#66222f] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors">
          <Repeat className="w-3.5 h-3.5" /> Buat Rotasi
        </button>
      </div>

      {data === null && <p className="py-12 text-center text-xs text-slate-400">Memuat...</p>}
      {data && !items.length && (
        <div className="bg-white rounded-2xl border border-slate-100 py-14 text-center">
          <Repeat className="w-8 h-8 mx-auto mb-2 text-slate-200" />
          <p className="text-xs text-slate-400">Belum ada rotasi — contoh: Grup A & Grup B tukaran Shift Pagi/Siang setiap minggu.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((r) => (
          <div key={r.id} data-testid={`rot-card-${r.id}`} className={`bg-white rounded-2xl border border-slate-100 p-4 space-y-3 ${!r.active ? "opacity-55" : ""}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display font-bold text-sm text-slate-900">{r.name}</h3>
                <p className="text-[10px] text-slate-400">Mulai {formatDateID(r.anchor_date)} • {r.member_count} karyawan</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 whitespace-nowrap">{r.freq_label}</span>
                <button data-testid={`rot-toggle-${r.id}`} onClick={() => toggleActive(r)} title={r.active ? "Nonaktifkan" : "Aktifkan"}
                  className={`relative inline-flex w-9 h-5 rounded-full transition-colors ${r.active ? "bg-emerald-400" : "bg-slate-200"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${r.active ? "left-[18px]" : "left-0.5"}`} />
                </button>
              </div>
            </div>
            {r.current?.length > 0 && (
              <div className="bg-slate-50 rounded-xl border border-slate-100 px-3 py-2.5 space-y-1">
                <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">Periode berjalan</p>
                {r.current.map((c) => (
                  <p key={c.group} className="text-[11px] text-slate-600"><b>{c.group}</b> → <span className="text-[#C4738A] font-semibold">{c.shift}</span></p>
                ))}
              </div>
            )}
            <div className="space-y-1">
              {(r.groups || []).map((g) => (
                <p key={g.name} className="text-[10px] text-slate-500"><b>{g.name}:</b> {(g.user_names || []).join(", ") || "-"}</p>
              ))}
            </div>
            <div className="flex gap-1 justify-end pt-1">
              <button data-testid={`rot-edit-${r.id}`} onClick={() => openForm(r)} title="Ubah" className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
              <button data-testid={`rot-del-${r.id}`} onClick={() => setDelTarget(r)} title="Hapus" className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      {delTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDelTarget(null)}>
          <div data-testid="rot-del-modal" className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-sm text-slate-900">Hapus Rotasi</h4>
              <button data-testid="rot-del-close" onClick={() => setDelTarget(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">Hapus rotasi <b>{delTarget.name}</b>? {delTarget.member_count} karyawan di dalamnya kembali memakai jadwal bawaan masing-masing. Absensi tercatat tetap aman.</p>
            <div className="flex gap-2 pt-1">
              <button data-testid="rot-del-cancel" onClick={() => setDelTarget(null)} className="flex-1 h-9 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold hover:border-slate-300">Batal</button>
              <button data-testid="rot-del-confirm" onClick={doDelete} className="flex-1 h-9 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 transition-colors">Ya, Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
